import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "@scripts/react/components/Button";
import { FlowStepper } from "@/components/layout/FlowStepper";
import { AnimateMotion } from "@/components/AnimateMotion";
import { VisualArea as VisualAreaWrapper } from "@/components/layout/VisualArea";
import { VisualAreaBottom as VisualAreaBottomWrapper } from "@/components/layout/VisualAreaBottom";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { useMobile } from "@/hooks/useMobile";
import { DEFAULT_DURATION } from "@/constants/motion";

interface FloatingSidebarProps {
    children: React.ReactNode;
}

function FloatingSidebar({ children }: FloatingSidebarProps) {
    const { hideSidebar } = useCoreApp();
    const isMobile = useMobile();

    return (
        <div className="relative flex min-h-0 w-[340px] flex-shrink-0 flex-col p-6 pl-0">
            <div className="shadow-soft relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-300 bg-white">
                <motion.div
                    className="relative flex min-h-0 flex-1 flex-shrink-0 flex-col"
                    initial={false}
                    animate={{
                        ...(isMobile && { height: hideSidebar ? 0 : "auto" }),
                        paddingTop: hideSidebar ? 0 : undefined,
                        paddingBottom: hideSidebar ? 0 : undefined
                    }}
                    transition={{
                        duration: DEFAULT_DURATION.slower,
                        ease: "easeInOut",
                        ...(isMobile && { height: { duration: DEFAULT_DURATION.slower } })
                    }}
                    style={{ overflow: "hidden" }}
                >
                    <AnimatePresence mode="wait">
                        {!hideSidebar && (
                            <motion.div
                                key="sidebar-content"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: DEFAULT_DURATION.slower, ease: "easeOut" }}
                                className="sidebarBody hideOnMobile min-h-0 flex-1"
                            >
                                {children}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </div>
    );
}

interface FloatingSidebarLayoutProps {
    VisualArea: React.ReactNode;
    Sidebar?: React.ReactNode;
    BottomContent?: React.ReactNode;
}

export function FloatingSidebarLayout({
    VisualArea,
    Sidebar,
    BottomContent
}: FloatingSidebarLayoutProps) {
    const sidebarNode = Sidebar ? <FloatingSidebar>{Sidebar}</FloatingSidebar> : undefined;
    const bottomNode = BottomContent ? (
        <VisualAreaBottomWrapper>{BottomContent}</VisualAreaBottomWrapper>
    ) : undefined;
    return (
        <AnimateMotion className="relative flex min-h-0 min-w-0 flex-1 flex-col md:flex-row">
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-3 pb-1 md:p-0">
                <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
                    <VisualAreaWrapper>{VisualArea}</VisualAreaWrapper>
                </div>
                {bottomNode && <div className="relative w-screen md:w-full">{bottomNode}</div>}
            </div>
            {sidebarNode}
        </AnimateMotion>
    );
}

export { FloatingSidebar };

interface FullScreenModalProps {
    title: string;
    description: string;
    onClose: () => void;
    children: React.ReactNode;
    showHeaderBorder?: boolean;
}

export function FullScreenModal({
    title,
    description,
    onClose,
    children,
    showHeaderBorder = false
}: FullScreenModalProps) {
    return (
        <AnimateMotion className="relative flex h-full min-h-0 w-full flex-col bg-white">
            <FlowStepper />
            <div
                className={`flex items-center justify-between px-6 ${
                    showHeaderBorder ? "border-b py-3" : "pt-4"
                } bg-white`}
            >
                <div className="flex items-center gap-2">
                    <h1 className="text-base font-semibold text-gray-900">{title}</h1>
                    <span className="text-sm text-gray-500">{description}</span>
                </div>
                <Button type="secondary" size="xsmall" onClick={onClose}>
                    <i className="far fa-times"></i>
                </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
        </AnimateMotion>
    );
}
